import { createContext, useEffect, useState } from 'react';
import {
  CartContextType,
  CartDetailType,
  ChildrenType,
} from '../interface/interface';
import { useAuth } from 'react-oidc-context';
import axios from '../config/axios';
import { userDetails } from '../utils/userDetail';
import showToast from '../components/ToastComponent';

const CartContext = createContext({} as CartContextType);

const CartContextProvider = ({ children }: ChildrenType) => {
  const { user } = useAuth();
  const [cartDetail, setCartDetails] = useState<CartDetailType | undefined>();

  const callCart = async () => {
    try {
      const token = user?.access_token;

      if (!token) {
        return false;
      }

      const userDetail = await userDetails(token);

      if (userDetail?.role !== 'Patients') return false;

      const response = await axios('/api/v1/cart', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const responseData = response?.data;
      setCartDetails(responseData);
    } catch (error) {
      console.error('Failed to fetch card details');
    }
  };

  async function addProductToCart(id: string, substituteForId?: string) {
    try {
      const data = {
        product_id: id,
        substituteForId,
      };

      const res = await axios.post('/api/v1/cart/add', data, {
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
        },
        validateStatus: (status) => {
          return status < 500;
        },
      });

      if (res.status === 200) {
        showToast({
          message: 'Item added to your cart',
          type: 'success',
        });

        await callCart();
      } else {
        let message = res.data.message;

        if (message === 'Email is invalid')
          message = `You cannot place orders as your email is invalid.
        Please update your email address in your profile. Note, this will also change your login username.`;

        showToast({
          message: message,
          type: 'warning',
          timeout: 6000,
        });
      }
    } catch (error: any) {
      //Do Nothing
      console.error(error);
      showToast({
        message: 'Failed to add item to the cart',
        type: 'error',
      });
    }
  }

  async function removeProductFromCart(id: string) {
    try {
      const data = {
        product_id: id,
      };

      const res = await axios.post('/api/v1/cart/remove', data, {
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
        },
        validateStatus: (status) => {
          return status < 505;
        },
      });

      if (res.status === 200) {
        showToast({
          message: 'Item removed from your cart',
          type: 'success',
        });
        await callCart();
      } else {
        showToast({
          message: res.data?.message,
          type: 'warning',
        });
      }
    } catch (error: any) {
      console.error(error);
      showToast({
        message: 'Failed to remove item from the cart',
        type: 'error',
      });
    }
  }

  useEffect(() => {
    callCart();
  }, []);

  return (
    <CartContext.Provider
      value={{
        cart: cartDetail,
        setCart: setCartDetails,
        addCart: addProductToCart,
        removeCart: removeProductFromCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export { CartContext, CartContextProvider };
